import { Typography } from '@iheartjane/reefer';
import type { MaskitoOptions } from '@maskito/core';
import { useMaskito } from '@maskito/react';

import { formatTime } from '@jane/shared/util';

const DATE_MASK: MaskitoOptions = {
  mask: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
};

const AMERICAN_DATE_REGEX = /^\d{2}\/\d{2}\/\d{4}$/;

const formatIncomingDate = (date: string) =>
  formatTime({
    time: date,
    outputFormat: 'MM/DD/YYYY',
    inputFormat: 'YYYY-MM-DD',
  });

// NB: only a complete MM/DD/YYYY value is converted. The mask leaves a partially
// typed value as bare digits ('04/2'), and `Date.parse` reads those as real dates
// — '04/2' is April 2 2001 — so without this guard a half-typed value would emit
// a plausible-looking birth date that callers can't tell apart from a finished one.
const formatOutgoingDate = (date: string) =>
  AMERICAN_DATE_REGEX.test(date)
    ? formatTime({
        time: date,
        outputFormat: 'YYYY-MM-DD',
        inputFormat: 'MM/DD/YYYY',
      })
    : date;

interface DateInputProps {
  className?: string;
  date: string;
  invalid: boolean;
  onChange: (arg: string) => void;
  placeholder?: string;
}

// This is a masked input component that does not implement form validations.
// This should only be used when it's necessary to render a date input outside of a form.
export const DateInput = ({
  date,
  onChange,
  invalid,
  className,
  placeholder,
}: DateInputProps) => {
  const dateRef = useMaskito({ options: DATE_MASK });

  return (
    <Typography>
      <input
        ref={dateRef}
        defaultValue={date ? formatIncomingDate(date) : ''}
        onInput={(event) =>
          onChange(formatOutgoingDate(event.currentTarget.value))
        }
        aria-invalid={invalid}
        placeholder={placeholder || 'mm/dd/yyyy'}
        type="tel"
        className={className}
      />
    </Typography>
  );
};
